import React from 'react';
import PropTypes from 'prop-types';

/**
 * InfoCard Component
 * 
 * Specialized card for displaying key metrics, statistics, or information.
 * Optimized for dashboard displays and summary views.
 * 
 * @example
 * // Basic info card
 * <InfoCard
 *   title="Total Users"
 *   value="1,234"
 *   icon={<UserIcon size="lg" />}
 * />
 * 
 * @example
 * // Info card with description and trend
 * <InfoCard
 *   title="Revenue"
 *   value="$12,345"
 *   description="+12.5% from last month"
 *   trend="up"
 *   icon={<DollarIcon size="lg" />}
 * />
 * 
 * @example
 * // Clickable info card
 * <InfoCard
 *   title="Websites"
 *   value="5"
 *   description="3 published"
 *   onClick={() => navigate('/websites')}
 *   hoverable
 * />
 */
const InfoCard = ({
  title,
  value,
  description,
  icon,
  trend,
  iconBgColor = 'bg-blue-100',
  iconColor = 'text-blue-600',
  onClick,
  hoverable = false,
  className = '',
  ...props
}) => {
  // Base classes
  const baseClasses = 'bg-white border border-gray-200 rounded-lg p-6 transition-all duration-200';
  
  // Hover classes
  const hoverClasses = (hoverable || onClick)
    ? 'hover:shadow-lg hover:border-gray-300 cursor-pointer'
    : '';
  
  // Trend indicator classes
  const trendClasses = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-600',
  };
  
  // Combine all classes
  const combinedClasses = [
    baseClasses,
    hoverClasses,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <div
      className={combinedClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyPress={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
      {...props}
    >
      <div className="flex items-center justify-between">
        {/* Left side: Icon and content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-4">
            {/* Icon */}
            {icon && (
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${iconBgColor}`}>
                <div className={iconColor}>
                  {icon}
                </div>
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Title */}
              {title && (
                <p className="text-sm font-medium text-gray-600 truncate">
                  {title}
                </p>
              )}
              
              {/* Value */}
              {value && (
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {value}
                </p>
              )}
              
              {/* Description */}
              {description && (
                <p className={`mt-1 text-sm ${trend ? trendClasses[trend] : 'text-gray-500'}`}>
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

InfoCard.propTypes = {
  /** Card title/label */
  title: PropTypes.string,
  /** Main value/metric to display */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Additional description or context */
  description: PropTypes.string,
  /** Icon element to display */
  icon: PropTypes.node,
  /** Trend indicator (affects description color) */
  trend: PropTypes.oneOf(['up', 'down', 'neutral']),
  /** Background color class for icon container */
  iconBgColor: PropTypes.string,
  /** Text color class for icon */
  iconColor: PropTypes.string,
  /** Click handler for interactive cards */
  onClick: PropTypes.func,
  /** Whether card should have hover effects */
  hoverable: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default InfoCard;
